import React, { ChangeEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router';
import {
    HentKjoretoySkjema,
    HentKjoretoySkjemaSkjemaData,
} from '../../components/HentKjoretoySkjema';
import { useApptekster } from '../../contexts/ApptekstContext';
import { useApi } from '../../hooks/useApi';
import {
    hentGyldigeKjoretoy,
    hentGyldigSkiltForKjoretoy,
} from '../../utils/api';
import {
    skiltbestillingsteg,
    useSkiltbestillingState,
} from './Skiltbestilling';
import { Stegveiviser } from '../../components/Stegveiviser';
import { SVVBreadcrumbLink } from '@komponentkassen/svv-breadcrumbs';
import { SVVSpinner } from '@komponentkassen/svv-spinner';
import { useEffectOnce } from '../../hooks/useEffectOnce';
import { SVVLayoutBanner } from '@komponentkassen/svv-layout';
import { SVVRadioGroup } from '@komponentkassen/svv-form';
import {
    SVVButtonCancel,
    SVVButton,
    SVVButtonRow,
} from '@komponentkassen/svv-button';

export const VelgSkiltstorrelse: React.FC = () => {
    const t = useApptekster();
    const navigate = useNavigate();
    const { handleSubmit } = useForm();
    const skjema = useForm<HentKjoretoySkjemaSkjemaData>();
    const api = useApi(hentGyldigSkiltForKjoretoy);
    const [feilmelding, setFeilmelding] = useState(false);
    const rettighetId = useSkiltbestillingState((state) => state.rettighetId);
    const kjennemerke = useSkiltbestillingState((state) => state.kjennemerke);

    const tegnkombinasjon = useSkiltbestillingState(
        (state) => state.tegnkombinasjon,
    );

    const setKjennemerke = useSkiltbestillingState(
        (state) => state.setKjennemerke,
    );

    const gyldigKjoretoyListe = useApi(hentGyldigeKjoretoy);

    useEffectOnce(() => {
        gyldigKjoretoyListe.hent(tegnkombinasjon);
    });

    const sokResultat = api.data?.ok && !skjema.formState.isDirty;

    useEffect(() => {
        if (sokResultat) {
            setFeilmelding(false);
        }
    }, [sokResultat, api.data, setKjennemerke]);

    if (!rettighetId) {
        return <Navigate to="/" />;
    }

    const kjoretoyListeLastet = gyldigKjoretoyListe.data;

    return (
        <>
            <SVVLayoutBanner>
                <SVVBreadcrumbLink
                    href="#/statusoversikt"
                    ariaLabel={t('personlig-kjennemerke.snarvei.status')}
                    className="intern-link"
                >
                    {t('personlig-kjennemerke.snarvei.status')}
                </SVVBreadcrumbLink>
                <h1>{t('personlig-kjennemerke.bestill.overskrift')}</h1>
            </SVVLayoutBanner>

            <SVVLayoutBanner secondary>
                <div className="form-layout spacing-bottom--medium">
                    <div className="form-layout__nav">
                        <Stegveiviser
                            steg={skiltbestillingsteg}
                            aktivtSteg={1}
                        />
                    </div>
                    <div className="form-layout__main">
                        <h2 className="spacing-bottom--xsmall tekstbredde">
                            {t('personlig-kjennemerke.bestill.steg.kjoretoy')}
                        </h2>
                        <p className="spacing-bottom--small">
                            {t(
                                'personlig-kjennemerke.bestill.kjoretoy.overskrift',
                                tegnkombinasjon,
                            )}
                        </p>

                        <div aria-live="polite">
                            {gyldigKjoretoyListe.laster && (
                                <SVVSpinner size="small" centered={false}>
                                    {t('personlig-kjennemerke.delt.laster')}
                                </SVVSpinner>
                            )}
                        </div>

                        {/* sokefelt vises kun om bruker har mer enn 8 kjoretoy. gyldigKjoretoyListe blir 0 om bruker har flere enn 15 kjoretoy */}
                        {kjoretoyListeLastet &&
                            (gyldigKjoretoyListe.data!.length > 8 ||
                                gyldigKjoretoyListe.data!.length === 0) && (
                                <HentKjoretoySkjema api={api} />
                            )}

                        <form
                            noValidate
                            className="form-group"
                            onSubmit={handleSubmit(() => {
                                if (kjennemerke.length > 0) {
                                    setFeilmelding(false);
                                    navigate('/skiltbestilling/storrelse');
                                } else {
                                    setFeilmelding(true);
                                }
                            })}
                        >
                            {sokResultat && (
                                <SVVRadioGroup
                                    name="velg-kjoretoy"
                                    data-hj-suppress={true}
                                    options={[
                                        {
                                            value: api.data!.kjennemerke,
                                            text: `${api.data!.kjennemerke} ${
                                                api.data!.kjoretoyMerke
                                            } ${api.data!.kjoretoyModell} (${
                                                api.data!.arsmodell
                                            })`,
                                        },
                                    ]}
                                    selected={kjennemerke}
                                    legend={t(
                                        'personlig-kjennemerke.bestill.sok.velg',
                                    )}
                                    id={'velg-kjoretoy'}
                                    errorMessage={
                                        feilmelding
                                            ? t(
                                                  'personlig-kjennemerke.bestill.sok.ikke-valgt',
                                              )
                                            : undefined
                                    }
                                    onChange={(
                                        e: ChangeEvent<HTMLInputElement>,
                                    ) => {
                                        setKjennemerke(e.target.value);
                                        setFeilmelding(false);
                                    }}
                                    required
                                />
                            )}

                            {/* vises kun om bruker har 8 eller ferre kjoretoy */}
                            {kjoretoyListeLastet &&
                                gyldigKjoretoyListe.data!.length > 0 &&
                                gyldigKjoretoyListe.data!.length < 9 && (
                                    <SVVRadioGroup
                                        name="velg-kjoretoy"
                                        data-hj-suppress={true}
                                        options={gyldigKjoretoyListe.data!.map(
                                            (gyldigKjoretoy) => ({
                                                value: gyldigKjoretoy.kjennemerke,
                                                text: `${gyldigKjoretoy.kjennemerke} ${gyldigKjoretoy.merkenavn} ${gyldigKjoretoy.modell} (${gyldigKjoretoy.arsmodell})`,
                                            }),
                                        )}
                                        selected={kjennemerke}
                                        legend={t(
                                            'personlig-kjennemerke.bestill.sok.velg',
                                        )}
                                        id={'velg-kjoretoy'}
                                        errorMessage={
                                            feilmelding
                                                ? t(
                                                      'personlig-kjennemerke.bestill.sok.ikke-valgt',
                                                  )
                                                : undefined
                                        }
                                        onChange={(
                                            e: ChangeEvent<HTMLInputElement>,
                                        ) => {
                                            setKjennemerke(e.target.value);
                                            setFeilmelding(false);
                                        }}
                                        required
                                    />
                                )}
                            <div className="svv-knapperad--spacing-top">
                                <SVVButtonRow>
                                    <SVVButton type="submit">
                                        {t('personlig-kjennemerke.delt.neste')}
                                    </SVVButton>
                                    <SVVButtonCancel
                                        href={`#status/${rettighetId}`}
                                    >
                                        {t('personlig-kjennemerke.delt.avbryt')}
                                    </SVVButtonCancel>
                                </SVVButtonRow>
                            </div>
                        </form>
                    </div>
                </div>
            </SVVLayoutBanner>
        </>
    );
};
